import { useCallback, useEffect, useMemo, useState } from 'react'
import { Activity, Database, GitCompareArrows, RefreshCw, Timer } from 'lucide-react'
import { liveOddsSupabase as supabase } from '../lib/supabase'
import { fetchReceiverStats } from '../lib/api'
import { getBookLogo } from '../lib/logoMaps'
import { PIPELINE_STORAGE_NOTES } from '../data/bookCoverage'
import './PipelineStorageWidget.css'

function timeAgo(dateStr) {
  if (!dateStr) return 'never'
  const diff = Date.now() - new Date(dateStr).getTime()
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
  return `${Math.floor(diff / 86400000)}d ago`
}

export default function PipelineStorageWidget() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [snapshotCount, setSnapshotCount] = useState(null)
  const [statusRows, setStatusRows] = useState([])
  const [lastRefreshed, setLastRefreshed] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)

    const [receiverStatsRes, statusRes] = await Promise.allSettled([
      fetchReceiverStats(),
      supabase
        ? supabase.from('scraper_status').select('bookmaker,status,delta_count,cycle_time_ms,updated_at,last_success').order('bookmaker')
        : Promise.resolve({ data: [], error: new Error('Supabase not configured') }),
    ])

    const receiverError = receiverStatsRes.status === 'fulfilled' && receiverStatsRes.value.status !== 'ok'
      ? new Error(receiverStatsRes.value.message || 'Receiver stats unavailable')
      : receiverStatsRes.status === 'rejected'
        ? receiverStatsRes.reason
        : null
    const firstError = receiverError

    if (firstError) {
      setError(firstError.message || String(firstError))
    } else {
      setError(null)
    }

    if (receiverStatsRes.status === 'fulfilled' && receiverStatsRes.value.status === 'ok') {
      const receiverData = receiverStatsRes.value.data || {}
      setSnapshotCount(Number(receiverData.total_odds) || 0)

      if (statusRes.status === 'fulfilled' && !statusRes.value.error && (statusRes.value.data || []).length > 0) {
        setStatusRows(statusRes.value.data || [])
      } else {
        const nowIso = new Date().toISOString()
        setStatusRows(
          Object.entries(receiverData.by_bookmaker || {}).map(([bookmaker, count]) => ({
            bookmaker,
            status: 'online',
            delta_count: 0,
            cycle_time_ms: null,
            updated_at: nowIso,
            last_success: nowIso,
            outcomes_count: Number(count) || 0,
          }))
        )
      }
    } else {
      setSnapshotCount(null)
      if (statusRes.status === 'fulfilled') setStatusRows(statusRes.value.data || [])
    }

    setLastRefreshed(new Date())
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 60000)
    return () => clearInterval(interval)
  }, [fetchData])

  const onlineBooks = useMemo(
    () => statusRows.filter((row) => row.status === 'online').length,
    [statusRows]
  )
  const totalBooks = statusRows.length
  const deltaSum = useMemo(
    () => statusRows.reduce((sum, row) => sum + (Number(row.delta_count) || 0), 0),
    [statusRows]
  )
  const averageCycleMs = useMemo(() => {
    const valid = statusRows.map((row) => Number(row.cycle_time_ms) || 0).filter(Boolean)
    if (valid.length === 0) return null
    return Math.round(valid.reduce((sum, value) => sum + value, 0) / valid.length)
  }, [statusRows])

  return (
    <div className="widget psw-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <Database size={16} />
          Pipeline Storage
          <span className="psw-badge">snapshot + delta logging</span>
        </h3>
        <button className="widget-refresh-btn" onClick={fetchData} title="Refresh storage metrics">
          <RefreshCw size={14} className={loading ? 'psw-spinner' : ''} />
        </button>
      </div>

      <div className="psw-summary">
        <div className="psw-stat-card">
          <span className="psw-stat-value">{snapshotCount != null ? snapshotCount.toLocaleString() : '—'}</span>
          <span className="psw-stat-label">live_odds snapshot rows</span>
        </div>
        <div className="psw-stat-card">
          <span className="psw-stat-value">{deltaSum.toLocaleString()}</span>
          <span className="psw-stat-label">delta_count sum</span>
        </div>
        <div className="psw-stat-card">
          <span className="psw-stat-value">{averageCycleMs != null ? `${averageCycleMs}ms` : '—'}</span>
          <span className="psw-stat-label">average cycle_time_ms</span>
        </div>
        <div className="psw-stat-card">
          <span className="psw-stat-value">{onlineBooks}/{totalBooks || '—'}</span>
          <span className="psw-stat-label">books reporting in scraper_status</span>
        </div>
      </div>

      {error && <div className="psw-error">{error}</div>}

      <div className="psw-grid">
        <section className="psw-panel">
          <div className="psw-panel-header">
            <span>Storage Lanes</span>
            <span className="psw-panel-meta">Cloud receiver writes</span>
          </div>
          <div className="psw-lane-list">
            {PIPELINE_STORAGE_NOTES.map((lane) => (
              <div key={lane.lane} className="psw-lane-card">
                <div className="psw-lane-top">
                  <span className="psw-lane-name">{lane.lane}</span>
                  <span className="psw-lane-mode">{lane.mode}</span>
                </div>
                <div className="psw-lane-detail">{lane.detail}</div>
              </div>
            ))}
          </div>
          <div className="psw-code-path">
            Source of truth: <code>scripts/receiver/rust/abet_receiver_cloud/src/main.rs</code>
          </div>
          <div className="psw-footnote">
            The receiver still writes `odds_history` deltas, but direct anonymous table scans are currently too heavy for the browser path. The dashboard uses lightweight poll telemetry instead of a full `odds_history` scan here.
          </div>
        </section>

        <section className="psw-panel">
          <div className="psw-panel-header">
            <span>Poll Telemetry</span>
            <span className="psw-panel-meta">scraper_status</span>
          </div>
          <div className="psw-telemetry">
            <div className="psw-telemetry-card">
              <Activity size={14} />
              <div>
                <div className="psw-telemetry-value">{deltaSum.toLocaleString()}</div>
                <div className="psw-telemetry-label">delta_count sum</div>
              </div>
            </div>
            <div className="psw-telemetry-card">
              <Timer size={14} />
              <div>
                <div className="psw-telemetry-value">{averageCycleMs != null ? `${averageCycleMs}ms` : '—'}</div>
                <div className="psw-telemetry-label">average cycle_time_ms</div>
              </div>
            </div>
            <div className="psw-telemetry-card">
              <GitCompareArrows size={14} />
              <div>
                <div className="psw-telemetry-value">30s / 60s</div>
                <div className="psw-telemetry-label">current poll cadence reality</div>
              </div>
            </div>
          </div>
          <div className="psw-status-list">
            {statusRows.slice(0, 10).map((row) => (
              <div key={row.bookmaker} className="psw-status-row">
                <div className="psw-status-name">
                  {getBookLogo(row.bookmaker) && (
                    <img
                      src={getBookLogo(row.bookmaker)}
                      alt=""
                      className="psw-book-logo"
                      onError={(event) => { event.target.style.display = 'none' }}
                    />
                  )}
                  <span>{row.bookmaker}</span>
                </div>
                <div className="psw-status-metrics">
                  <span>{row.delta_count || 0} Δ</span>
                  <span>{row.cycle_time_ms || 0}ms</span>
                  <span>{timeAgo(row.last_success || row.updated_at)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="psw-footnote">
            `scraper_status` is telemetry, not the final truth source for every VM book. Use receiver stats + live_odds together.
          </div>
        </section>

        <section className="psw-panel psw-panel-wide">
          <div className="psw-panel-header">
            <span>Polling + Delta Notes</span>
            <span className="psw-panel-meta">What is currently safe to read in dashboard</span>
          </div>
          <div className="psw-note-list">
            <div className="psw-note-card">
              <span className="psw-note-title">Snapshot path</span>
              <span className="psw-note-detail">`live_odds` remains cheap enough for anonymous dashboard reads and powers the main odds table.</span>
            </div>
            <div className="psw-note-card">
              <span className="psw-note-title">Delta path</span>
              <span className="psw-note-detail">`odds_history` is still the append-only delta log, but broad anonymous scans currently hit a statement timeout.</span>
            </div>
            <div className="psw-note-card">
              <span className="psw-note-title">Telemetry path</span>
              <span className="psw-note-detail">`scraper_status` is the lightweight proof that polling is happening and deltas are being recorded cycle by cycle.</span>
            </div>
            <div className="psw-note-card">
              <span className="psw-note-title">Next hardening step</span>
              <span className="psw-note-detail">If we want cheap browser-side line-history views at scale, add an indexed view or a server-side API over `odds_history` instead of direct full-table reads.</span>
            </div>
          </div>
        </section>
      </div>

      <div className="widget-footer">
        <span>{lastRefreshed ? `Refreshed ${lastRefreshed.toLocaleTimeString()}` : 'Loading...'}</span>
        <span>Delta logging preserves space by only appending changed odds rows</span>
      </div>
    </div>
  )
}
